'use client';

import { useCallback, useEffect, useState } from 'react';

const PRICING_BAND_STORAGE_KEY = 'ofa-pricing-band-selection';

export type PricingBandSelection = {
  bandId: string;
  timestamp: number;
};

/**
 * Custom hook to manage pricing band selection persistence across the session
 * Stores the last selected pricing band in sessionStorage
 */
export const usePricingBandPersistence = () => {
  const [lastSelectedBand, setLastSelectedBand] = useState<string | null>(null);

  // Load the last selected band from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(PRICING_BAND_STORAGE_KEY);
      if (stored) {
        const selection: PricingBandSelection = JSON.parse(stored);
        setLastSelectedBand(selection.bandId);
      }
    } catch (error) {
      console.warn('Failed to load pricing band selection from sessionStorage:', error);
    }
  }, []);

  // Save the selected band to sessionStorage
  const saveBandSelection = useCallback((bandId: string) => {
    try {
      const selection: PricingBandSelection = {
        bandId,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(PRICING_BAND_STORAGE_KEY, JSON.stringify(selection));
      setLastSelectedBand(bandId);
    } catch (error) {
      console.warn('Failed to save pricing band selection to sessionStorage:', error);
    }
  }, []);

  // Clear the stored band selection
  const clearBandSelection = useCallback(() => {
    try {
      sessionStorage.removeItem(PRICING_BAND_STORAGE_KEY);
      setLastSelectedBand(null);
    } catch (error) {
      console.warn('Failed to clear pricing band selection from sessionStorage:', error);
    }
  }, []);

  return {
    lastSelectedBand,
    saveBandSelection,
    clearBandSelection,
  };
};
